import { DateTime } from 'luxon';
import type {
  AnnouncementApp,
  AnnouncementPerson,
  AnnouncementPlatform,
  AnnouncementTarget,
} from '../../../types/api/announcements.types';

/**
 * How a viewer is called everywhere on the seen-by screens. `name` wins when
 * the backend has one; the rest are people who only ever filled in the split
 * fields, or nothing at all.
 */
export const viewerName = ({ name, firstName, lastName }: AnnouncementPerson) =>
  name?.trim() ||
  [firstName, lastName].filter(Boolean).join(' ') ||
  'Unknown user';

const PLATFORM_LABEL: Record<AnnouncementPlatform, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

export const platformLabel = (platform: AnnouncementPlatform) =>
  PLATFORM_LABEL[platform] ?? platform;

/** "merchant iOS", or just "iOS" when every target is the same app. */
export const pairLabel = (
  app: AnnouncementApp,
  platform: AnnouncementPlatform,
  withApp = true
) => (withApp ? `${app} ${platformLabel(platform)}` : platformLabel(platform));

/**
 * The targets in words, one app at a time: "merchant iOS, Android, Web ·
 * tablet Android". Grouped because seven "merchant/..." chips say the app
 * seven times and the platforms are what differ.
 */
export const targetsSummary = (targets: AnnouncementTarget[]) => {
  const byApp = new Map<AnnouncementApp, AnnouncementPlatform[]>();
  targets.forEach(({ app, platform }) =>
    byApp.set(app, [...(byApp.get(app) ?? []), platform])
  );
  return [...byApp]
    .map(
      ([app, platforms]) => `${app} ${platforms.map(platformLabel).join(', ')}`
    )
    .join(' · ');
};

/**
 * A moment as the activity feed says it: "just now", "12m ago", "2h ago" for
 * today's, then a date - "Oct 25", with the year only once it is not this one.
 */
export const formatSeenAt = (iso: string | null | undefined) => {
  if (!iso) return '-';
  const date = DateTime.fromISO(iso).toLocal();
  if (!date.isValid) return '-';

  const minutes = Math.floor(-date.diffNow('minutes').minutes);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h ago`;

  return date.hasSame(DateTime.local(), 'year')
    ? date.toFormat('LLL d')
    : date.toFormat('LLL d, yyyy');
};

/** The full moment, for tooltips: "Oct 25, 2026, 3:04 PM". */
export const formatSeenAtFull = (iso: string | null | undefined) => {
  if (!iso) return '-';
  const date = DateTime.fromISO(iso).toLocal();
  return date.isValid ? date.toFormat('LLL d, yyyy, h:mm a') : '-';
};

/** "Oct 14" or "Oct 14, 2025", for a day without a time. */
export const formatDay = (iso: string | null | undefined) => {
  if (!iso) return '-';
  const date = DateTime.fromISO(iso).toLocal();
  if (!date.isValid) return '-';
  return date.hasSame(DateTime.local(), 'year')
    ? date.toFormat('LLL d')
    : date.toFormat('LLL d, yyyy');
};
